import { useCallback, useMemo, useState } from "react";
import type { BeneficiaryDetail } from "@/domain/beneficiary";
import { useBeneficiaryDropdownSearch } from "@/application/queries";
import { useOrganizationStore } from "@/stores/useOrganizationStore";

interface BeneficiaryDropdownOption {
  value: string;
  label: string;
  disabled?: boolean;
  hasQuota?: boolean;
}

interface UseBeneficiaryDropdownOptions {
  enabled?: boolean;
  onlyWithQuota?: boolean;
  pageSize?: number;
}

interface UseBeneficiaryDropdownReturn {
  options: BeneficiaryDropdownOption[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  searchQuery: string;
  handleSearch: (query: string) => void;
  handleLoadMore: () => void;
  getBeneficiaryByUuid: (uuid: string) => BeneficiaryDetail | undefined;
  reset: () => void;
}

export const useBeneficiaryDropdown = (
  options: UseBeneficiaryDropdownOptions = {},
): UseBeneficiaryDropdownReturn => {
  const { enabled = true, onlyWithQuota = false, pageSize = 20 } = options;
  const orgId = useOrganizationStore((state) => state.selectedOrganizationUuid ?? "");
  const [searchQuery, setSearchQuery] = useState("");

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error: queryError,
  } = useBeneficiaryDropdownSearch(orgId, searchQuery, { enabled, pageSize });

  const beneficiaries = useMemo(() => data?.pages.flatMap((p) => p.beneficiaries) ?? [], [data]);

  const dropdownOptions: BeneficiaryDropdownOption[] = useMemo(
    () =>
      beneficiaries.map((beneficiary) => {
        const hasQuota = beneficiary.has_available_quota ?? false;

        return {
          value: beneficiary.uuid,
          label: beneficiary.name,
          disabled: onlyWithQuota && !hasQuota,
          hasQuota: !!hasQuota,
        };
      }),
    [beneficiaries, onlyWithQuota],
  );

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleLoadMore = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  const getBeneficiaryByUuid = useCallback(
    (uuid: string): BeneficiaryDetail | undefined => beneficiaries.find((p) => p.uuid === uuid),
    [beneficiaries],
  );

  const reset = useCallback(() => {
    setSearchQuery("");
  }, []);

  return {
    options: dropdownOptions,
    loading: isLoading,
    loadingMore: isFetchingNextPage,
    error: queryError ? "error.loading_beneficiaries" : null,
    hasMore: !!hasNextPage,
    searchQuery,
    handleSearch,
    handleLoadMore,
    getBeneficiaryByUuid,
    reset,
  };
};
